/**
 * LogResubmittalModal — record that the GC has submitted a revised package.
 *
 * Creates the next submittal_revisions row (Rev N+1) stamped with the received
 * date, then uploads the GC's files to that revision. The team follows up with
 * "Log Review" on the new revision once they've reviewed it.
 *
 * Opens via CustomEvent('open-log-resubmittal', { detail: { submittal, parentRevision } }):
 *   submittal      — full submittals row (id, contract_id, project_id, …)
 *   parentRevision — the reviewed revision that triggered the resubmittal
 */

import { useEffect, useRef, useState } from 'react'
import { createSubmittalRevision, updateSubmittal, submittalLabel } from '../services/submittals.js'
import { uploadRevisionFile } from '../services/submittalFiles.js'
import { createThreadActivity } from '../services/threads.js'

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }

function FileItem({ name, status }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid rgba(7,39,45,0.07)' }}>
      <span style={{ fontSize: 13 }}>📎</span>
      <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      <span style={{ fontFamily: '"DM Mono",sans-serif', fontSize: 10, color: status === 'done' ? '#1D9E75' : status === 'error' ? '#c0392b' : '#7a9aaa', flexShrink: 0 }}>
        {status === 'done' ? '✓' : status === 'error' ? '✕' : '…'}
      </span>
    </div>
  )
}

export default function LogResubmittalModal() {
  const [open, setOpen]               = useState(false)
  const [submittal, setSubmittal]     = useState(null)
  const [parentRevision, setParentRevision] = useState(null)

  const [dateReceived, setDateReceived] = useState('')
  const [note, setNote]               = useState('')
  const [files, setFiles]             = useState([]) // { file, status: 'pending'|'done'|'error' }

  const [saving, setSaving]           = useState(false)
  const [error, setError]             = useState(null)
  const [done, setDone]               = useState(false)
  const fileRef = useRef(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setSubmittal(d.submittal || null)
      setParentRevision(d.parentRevision || null)
      setDateReceived('')
      setNote('')
      setFiles([])
      setError(null); setSaving(false); setDone(false); setOpen(true)
    }
    window.addEventListener('open-log-resubmittal', onOpen)
    return () => window.removeEventListener('open-log-resubmittal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open && !saving) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, saving])

  function addFiles(e) {
    const picked = Array.from(e.target.files || [])
    setFiles(prev => [...prev, ...picked.map(f => ({ file: f, status: 'pending' }))])
    if (fileRef.current) fileRef.current.value = ''
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx))
  }

  async function handleSave() {
    if (!submittal || !dateReceived) { setError('Date received is required.'); return }
    setSaving(true); setError(null)
    try {
      // 1. Create Rev N+1
      const rev = await createSubmittalRevision(submittal.id, {
        date_received: dateReceived || null,
        review_comments: note || null,
      })

      // 2. Upload each file as the resubmitted PACKAGE, updating status per file
      for (let i = 0; i < files.length; i++) {
        try {
          await uploadRevisionFile(rev.id, submittal.id, files[i].file, 'package')
          setFiles(prev => prev.map((f, j) => j === i ? { ...f, status: 'done' } : f))
        } catch {
          setFiles(prev => prev.map((f, j) => j === i ? { ...f, status: 'error' } : f))
        }
      }

      // 3. Flip the submittal to "received, awaiting review": clear the prior review disposition
      //    (it no longer reflects the current round) and stamp the new received date.
      await updateSubmittal(submittal.id, { disposition: null, date_received: dateReceived || null, date_returned: null }).catch(() => {})

      // 4. Auto-log a thread activity (template note) for the resubmittal.
      const { ctx } = await import('../outline/ctx.js')
      if (submittal.thread_airtable_id) {
        const vN = ((rev && rev.revision_number) || 0) + 1
        try { await createThreadActivity(submittal.thread_airtable_id, 'Action Taken', dateReceived, 'Resubmittal (v' + vN + ') received on ' + dateReceived + '.', ctx.currentMemberId || null) } catch (_) { /* non-fatal */ }
        if (ctx.caThreadCache) ctx.caThreadCache[submittal.thread_airtable_id] = undefined
      }

      // 5. Invalidate caches (list + revision chain) so the row + revisions refresh in place.
      if (ctx.submittalCache && submittal.contract_id) ctx.submittalCache[submittal.contract_id] = undefined
      if (ctx.submittalRevCache) ctx.submittalRevCache[submittal.id] = undefined
      window.dispatchEvent(new Event('outline-render'))
      setDone(true)
      setSaving(false)
    } catch (ex) {
      setError(ex.message || 'Failed to log resubmittal')
      setSaving(false)
    }
  }

  if (!open) return null

  const specLabel = submittal ? submittalLabel(submittal) : 'Submittal'
  const parentRevNum = parentRevision?.revision_number ?? 0

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="LogResubmittalModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 500, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Log Resubmittal</div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>
              {specLabel} &mdash; responding to Rev {parentRevNum} review
            </div>
          </div>
          <button onClick={() => !saving && setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        {done ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ padding: '10px 12px', background: '#f0faf4', border: '0.5px solid #1D9E7533', borderRadius: 4 }}>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#1D9E75', fontWeight: 600 }}>✓ Resubmittal logged</div>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 4 }}>
                A new revision has been created. Use <strong>Log Review</strong> on it once you've reviewed the package.
              </div>
            </div>
            {files.length > 0 && (
              <div>
                {files.map((f, i) => <FileItem key={i} name={f.file.name} status={f.status} />)}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setOpen(false)} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Date received */}
            <div>
              <label style={labelStyle}>Date received from GC <span style={{ color: '#c0392b' }}>*</span></label>
              <input type="date" style={inputStyle} value={dateReceived} onChange={e => setDateReceived(e.target.value)} />
            </div>

            {/* Optional note */}
            <div>
              <label style={labelStyle}>Note (optional)</label>
              <input style={inputStyle} placeholder="e.g. Submitted via email with revised drawings" value={note} onChange={e => setNote(e.target.value)} />
            </div>

            {/* File upload */}
            <div>
              <label style={labelStyle}>GC Package Files</label>
              <div style={{ border: '0.5px dashed rgba(7,39,45,0.22)', borderRadius: 4, padding: '10px 12px', background: '#f9fbfc' }}>
                {files.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {files.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid rgba(7,39,45,0.07)' }}>
                        <span style={{ fontSize: 13 }}>📎</span>
                        <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</span>
                        <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 14, padding: 0 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={() => fileRef.current?.click()} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  + Add files
                </button>
                <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={addFiles} />
                <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: '#9bb2b7', marginTop: 4 }}>
                  PDFs, DWG, Word, images — any file type accepted
                </div>
              </div>
            </div>

            {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button onClick={() => setOpen(false)} disabled={saving} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving || !dateReceived} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: saving || !dateReceived ? 'rgba(7,39,45,0.18)' : '#07272D', color: saving || !dateReceived ? 'rgba(7,39,45,0.4)' : '#fff', border: 'none', borderRadius: 4, cursor: saving || !dateReceived ? 'default' : 'pointer' }}>
                {saving ? 'Saving…' : 'Log Resubmittal'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
